import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef, useCallback } from 'react';
import { Stack, Text, Paper, Divider, LoadingOverlay, Group, ActionIcon, Pagination } from '@mantine/core';
import { useGroupActivity } from '../../hooks/useGroupActivity';
import { useAuthInfo } from '@propelauth/react';
import { env } from '../../utils/env';
import { useSettlements } from '../../hooks/useSettlements';
import TrashIcon from '../../assets/icons/trash.svg';

// Debug utility
const DEBUG = env.DEBUG_MODE;
const debugLog = (...args: any[]) => {
  if (DEBUG) {
    console.log('[GroupActivityFeed]', ...args);
  }
};

// We no longer need ExtendedActivityMetadata since we added settlement_id to the base interface
// interface ExtendedActivityMetadata { ... }

interface GroupActivityFeedProps {
  groupId: string;
  onActivitiesLoaded?: (hasActivities: boolean) => void;
  onSettlementDeleted?: () => void;
  onActivitiesSeen?: () => void;
  forceRefreshTrigger?: number;
  lastViewedAt?: string; // Last time activities were viewed
}

// Use forwardRef to expose methods to parent component
const GroupActivityFeed = forwardRef<
  { markAsSeen: () => Promise<any> },
  GroupActivityFeedProps
>(({
  groupId,
  onActivitiesLoaded,
  onSettlementDeleted,
  onActivitiesSeen,
  forceRefreshTrigger = 0,
  lastViewedAt,
}, ref) => {
  const { accessToken } = useAuthInfo();
  const refreshCountRef = useRef(0);
  const emptyStateCheckedRef = useRef(false);
  const [fixedActivities, setFixedActivities] = useState<any[]>([]);
  const previousLastViewedAtRef = useRef<string | undefined>(lastViewedAt);
  // Keep track of activity IDs that were manually marked as seen
  const markedActivityIdsRef = useRef<Set<string>>(new Set());
  // Store timestamp when activities were marked as seen
  const markedAsSeenAtRef = useRef<Date | null>(null);

  const {
    activities: originalActivities,
    loading,
    error,
    refresh,
    markAsSeen,
    // Add pagination state and handlers from the hook
    currentPage,
    totalPages,
    // totalItems, // if needed
    // pageSize, // if needed for display or page size selector
    handlePageChange
  } = useGroupActivity(groupId, accessToken || '');

  const {
    deleteSettlement
  } = useSettlements({ groupId: parseInt(groupId), accessToken: accessToken || '' });

  // Reset the marked activity IDs when lastViewedAt changes from server
  useEffect(() => {
    if (lastViewedAt !== previousLastViewedAtRef.current) {
      console.log(`[GroupActivityFeed] lastViewedAt changed from ${previousLastViewedAtRef.current} to ${lastViewedAt}`);
      previousLastViewedAtRef.current = lastViewedAt;
      // If we received a new lastViewedAt from server, reset our local tracking
      markedActivityIdsRef.current.clear();
      markedAsSeenAtRef.current = null;
    }
  }, [lastViewedAt]);

  // Handle when activities load
  useEffect(() => {
    if (!loading && originalActivities) {
      const hasActivities = originalActivities.length > 0;

      debugLog('Activities loaded, notifying parent', {
        hasActivities,
        activityCount: originalActivities.length,
        groupId,
        lastViewedAt,
        markedActivityIds: markedActivityIdsRef.current.size
      });

      if (onActivitiesLoaded) {
        onActivitiesLoaded(hasActivities);
      }

      // Record that we've checked for empty state
      emptyStateCheckedRef.current = true;
    }
  }, [loading, originalActivities, onActivitiesLoaded, groupId, lastViewedAt]);

  // Handle force refresh from parent
  useEffect(() => {
    if (forceRefreshTrigger > 0 && forceRefreshTrigger !== refreshCountRef.current) {
      debugLog('Force refresh triggered', {
        trigger: forceRefreshTrigger,
        previous: refreshCountRef.current,
        groupId
      });

      refreshCountRef.current = forceRefreshTrigger;
      refresh(currentPage); // Refresh current page
    }
  }, [forceRefreshTrigger, refresh, groupId, currentPage]); // Added currentPage

  // Fix activities when they come from the hook
  useEffect(() => {
    if (!originalActivities?.length) {
      setFixedActivities([]);
      return;
    }

    const hasActivitiesWithMissingMetadata = originalActivities.some(
      (a: any) => a.type === 'settlement_created' && (!a.activity_metadata || !a.activity_metadata.settlement_id)
    );

    if (hasActivitiesWithMissingMetadata) {
      console.log('[GroupActivityFeed] Found settlement activities with missing metadata, fixing...');

      // Create a copy with fixed metadata
      const fixed = originalActivities.map((activity: any) => {
        // Only fix settlement activities with missing metadata
        if (activity.type !== 'settlement_created') return activity;

        // Use existing metadata or create new object
        const metadata = activity.activity_metadata || {};

        // Ensure settlement_id exists in metadata
        if (!metadata.settlement_id && activity.settlement_id) {
          console.log(`[GroupActivityFeed] Fixing activity ${activity.id} by adding settlement_id from direct property`);
          return {
            ...activity,
            activity_metadata: {
              ...metadata,
              settlement_id: activity.settlement_id
            }
          };
        }

        // Try to extract settlement ID from text as last resort
        if (!metadata.settlement_id && activity.text && activity.text.includes('settled')) {
          const match = activity.text.match(/settlement\s*#?(\d+)/i);
          if (match && match[1]) {
            const extractedId = parseInt(match[1], 10);
            if (!isNaN(extractedId)) {
              console.log(`[GroupActivityFeed] Fixing activity ${activity.id} by extracting settlement_id ${extractedId} from text`);
              return {
                ...activity,
                activity_metadata: {
                  ...metadata,
                  settlement_id: extractedId
                }
              };
            }
          }
        }

        return activity;
      });

      console.log('[GroupActivityFeed] Finished fixing settlement activity metadata');
      setFixedActivities(fixed);
    } else {
      // No fixes needed, just use the original
      setFixedActivities(originalActivities);
    }
  }, [originalActivities]);

  // Use the fixed activities for all operations
  const activities = fixedActivities;

  // Enhanced isUnread function to check activity status
  const isUnread = useCallback((activity: any): boolean => {
    // If this specific activity ID is in our marked set, it's not unread
    if (markedActivityIdsRef.current.has(activity.id)) {
      return false;
    }

    // If we have a marked-as-seen timestamp and this activity came after that, show as unread
    if (markedAsSeenAtRef.current && activity.created_at) {
      const activityDate = new Date(activity.created_at);
      if (activityDate > markedAsSeenAtRef.current) {
        // This activity arrived after we marked others as seen, so show as unread
        return true;
      }
    }

    // If no lastViewedAt provided, nothing is unread
    if (!lastViewedAt || !activity.created_at) {
      return false;
    }

    try {
      // Compare timestamps
      const activityDate = new Date(activity.created_at);
      const lastViewedDate = new Date(lastViewedAt);

      const result = activityDate > lastViewedDate;

      // Only log for unread activities to reduce noise
      if (result) {
        console.log(`[GroupActivityFeed] Activity ${activity.id} is unread: ${activityDate.toISOString()} > ${lastViewedDate.toISOString()}`);
      }

      return result;
    } catch (err) {
      console.error(`[GroupActivityFeed] Error checking unread status for activity ${activity?.id}:`, err);
      return false;
    }
  }, [lastViewedAt]);

  // Helper to determine if an activity is a valid settlement
  const getSettlementId = (activity: any): number | null => {
    // Only consider activities explicitly of type 'settlement_created'
    if (activity.type !== 'settlement_created') {
      return null;
    }

    // First check for direct settlement_id property if it exists
    if (typeof activity.settlement_id === 'number' && !isNaN(activity.settlement_id)) {
      return activity.settlement_id;
    }

    // Then check metadata
    if (activity.activity_metadata) {
      // Check if metadata contains a valid settlement_id
      const settlementId = activity.activity_metadata.settlement_id;
      if (typeof settlementId === 'number' && !isNaN(settlementId)) {
        return settlementId;
      }
    }

    // Handle backend data inconsistency by extracting ID from text if needed
    if (activity.text && activity.text.includes('settled')) {
      // Try to find a settlement ID in the text using regex
      // Format typically might be "... settlement #123 ..." or similar pattern
      const match = activity.text.match(/settlement\s*#?(\d+)/i);
      if (match && match[1]) {
        const extractedId = parseInt(match[1], 10);
        return !isNaN(extractedId) ? extractedId : null;
      }
    }

    return null;
  };

  // Check if a settlement is active (not cancelled)
  const isSettlementActive = (activity: any): boolean => {
    // If activity has a deleted marker in metadata, treat as cancelled
    if (activity.activity_metadata?.deleted) {
      return false;
    }

    // If activity has a status field directly, use that
    if (activity.status === 'cancelled') {
      return false;
    }

    // If metadata has a status field, use that
    if (activity.activity_metadata?.status === 'cancelled') {
      return false;
    }

    // Default to assuming it's active if we can't determine otherwise
    return true;
  };

  // Enhanced handler with improved validation and logging
  const handleDeleteSettlement = async (settlementId: number | null) => {
    if (typeof settlementId !== 'number' || isNaN(settlementId)) {
      console.error('[GroupActivityFeed] Cannot delete settlement: invalid or missing settlementId', {
        receivedId: settlementId,
        type: typeof settlementId
      });
      return;
    }

    console.log(`[GroupActivityFeed] Attempting to delete settlement with ID: ${settlementId}`);
    try {
      const success = await deleteSettlement(settlementId);
      if (success) {
        console.log(`[GroupActivityFeed] Successfully deleted settlement ${settlementId}`);

        // Refresh the activity feed
        refresh(currentPage);

        // Call the dedicated settlement deletion callback if available
        if (onSettlementDeleted) {
          console.log('[GroupActivityFeed] Calling onSettlementDeleted to trigger data refresh');
          onSettlementDeleted();
        } else if (onActivitiesLoaded) {
          // Fallback to original callback for backward compatibility
          console.log('[GroupActivityFeed] Notifying parent via onActivitiesLoaded (legacy)');
          onActivitiesLoaded(true);
        }
      } else {
        console.warn(`[GroupActivityFeed] deleteSettlement returned false for settlement ${settlementId}`);
      }
    } catch (err) {
      console.error('[GroupActivityFeed] Error deleting settlement:', err);
    }
  };

  // Create a dedicated function to mark activities as seen and trigger parent callback
  const handleActivitiesSeen = useCallback((): Promise<any> => {
    // Only proceed if we have activities to mark
    if (activities.length === 0) {
      console.log('[GroupActivityFeed] No activities to mark as seen');
      return Promise.resolve();
    }

    console.log('[GroupActivityFeed] Marking activities as seen via handleActivitiesSeen');

    // Store the current time as our marked-as-seen timestamp
    markedAsSeenAtRef.current = new Date();

    // Store all current activity IDs that should be marked as read
    activities.forEach(activity => {
      // Only mark as read if not already read
      if (isUnread(activity)) {
        markedActivityIdsRef.current.add(activity.id);
      }
    });

    console.log(`[GroupActivityFeed] Marked ${markedActivityIdsRef.current.size} activities as seen locally`);

    // Force a re-render to update the UI immediately
    const dummyEvent = new CustomEvent('force-rerender');
    window.dispatchEvent(dummyEvent);

    // Call the hook's markAsSeen method to update on the server
    return markAsSeen().then(() => {
      // If the parent component provided the onActivitiesSeen callback, call it
      if (onActivitiesSeen) {
        console.log('[GroupActivityFeed] Calling parent onActivitiesSeen callback');
        onActivitiesSeen();
      }
    }).catch((err) => {
      console.warn('[GroupActivityFeed] Error in handleActivitiesSeen:', err);
      return Promise.resolve(); // Still resolve to avoid breaking chains
    });
  }, [activities, isUnread, markAsSeen, onActivitiesSeen]);

  // Listen for our custom force-rerender event
  useEffect(() => {
    const forceRerender = () => {
      // This empty callback forces React to re-evaluate the component
      // when the event fires
      // A more direct way to cause re-render if needed: setFixedActivities(prev => [...prev]);
    };
    window.addEventListener('force-rerender', forceRerender);
    return () => {
      window.removeEventListener('force-rerender', forceRerender);
    };
  }, []);

  // Expose the markAsSeen function via ref
  useImperativeHandle(ref, () => ({
    markAsSeen: () => {
      console.log('[GroupActivityFeed] markAsSeen called via ref');
      return handleActivitiesSeen();
    }
  }), [handleActivitiesSeen]);

  // Handle errors with detailed logging
  if (error) {
    console.error(`[GroupActivityFeed] Activity feed error for group ${groupId}:`, error);
    return (
      <Paper p="md" style={{ textAlign: 'center' }}>
        <Text color="red">Error loading activity feed: {error}</Text>
      </Paper>
    );
  }

  // Show loader only on initial load
  if (loading && !activities?.length) {
    debugLog(`[GroupActivityFeed] Showing loading state for group ${groupId}`);
    return (
      <Paper p="md" style={{ textAlign: 'center', position: 'relative', minHeight: '100px' }}>
        <LoadingOverlay visible={true} />
      </Paper>
    );
  }

  // Handle empty state with detailed logging
  if (!activities || activities.length === 0) {
    // Log differently based on whether we've attempted to load before
    if (emptyStateCheckedRef.current) {
      debugLog(`[GroupActivityFeed] No activities found for group ${groupId} after loading`);
    } else {
      debugLog(`[GroupActivityFeed] No activities available for group ${groupId} (initial state)`);
    }

    return (
      <Paper p="md" style={{ textAlign: 'center' }}>
        <Text c="dimmed">No activity yet</Text>
      </Paper>
    );
  }

  // We have activities to display
  debugLog(`[GroupActivityFeed] Rendering ${activities.length} activities for group ${groupId}, markedActivityIds: ${markedActivityIdsRef.current.size}`);

  return (
    <Stack gap="md" w="100%">
      {activities.map((activity, index) => {
        // Get settlement ID using our helper function
        const settlementId = getSettlementId(activity);
        const settleActive = isSettlementActive(activity);
        const unread = isUnread(activity);

        return (
          <React.Fragment key={activity.id}>
            <Paper p="sm" withBorder style={{ borderColor: unread ? '#228be6' : undefined, borderWidth: unread ? 2 : 1 }}>
              <Group justify="space-between" align="flex-start">
                <div style={{ flex: 1 }}>
                  <Text dangerouslySetInnerHTML={{ __html: activity.text }} style={{ color: unread ? '#228be6' : undefined }} fw={unread ? 600 : 400} />
                  <Text size="xs" c="dimmed">{new Date(activity.created_at).toLocaleString()}</Text>
                </div>

                {/* Show delete button only for active settlement activities with valid IDs */}
                {activity.type === 'settlement_created' && settlementId !== null && settleActive && (
                  <ActionIcon
                    color="red"
                    variant="subtle"
                    onClick={() => handleDeleteSettlement(settlementId)}
                    title="Delete settlement"
                    data-testid={`delete-settlement-${settlementId}`}
                  >
                    <img src={TrashIcon} alt="Delete" width={16} height={16} />
                  </ActionIcon>
                )}
              </Group>
            </Paper>
            {index < activities.length - 1 && <Divider />}
          </React.Fragment>
        );
      })}
      {/* Add Pagination controls */}
      {totalPages > 1 && (
        <Group justify="center" mt="md">
          <Pagination total={totalPages} value={currentPage} onChange={handlePageChange} />
        </Group>
      )}
    </Stack>
  );
});

export default GroupActivityFeed; 